// ---------------------------------------------------------------------------
// DiCharts SDK – Public Types
// ---------------------------------------------------------------------------

import type {
  OHLCTuple,
  CandleStyle,
  OverlayConfig,
  SubPaneConfig,
  DiChartInstance,
} from '../../dicharts-lib/src';

export type { OHLCTuple, CandleStyle, OverlayConfig, SubPaneConfig, DiChartInstance };

// ---------------------------------------------------------------------------
// Widget configuration
// ---------------------------------------------------------------------------

export interface ToolbarConfig {
  stylePicker?: boolean;
  timeframes?: boolean;
  indicators?: boolean;
  layout?: boolean;
  settings?: boolean;
  fullscreen?: boolean;
  themeToggle?: boolean;
}

export interface StreamingConfig {
  /** Timeframe string (e.g. '1s', '1m', '5m', '15m', '1h', '4h', '1d'). */
  timeframe: string;
}

export interface DataSource {
  /** Called to load historical candles. */
  getHistory(
    symbol: string,
    timeframe: string,
    count: number,
  ): Promise<{ candles: OHLCTuple[]; volumes: number[] }>;

  /** Called to subscribe to live ticks. Return a cleanup function. */
  subscribe(
    symbol: string,
    timeframe: string,
    onTick: (tick: TickInput) => void,
  ): () => void;
}

export interface TickInput {
  timestamp: number;
  price: number;
  volume?: number;
}

export interface WidgetConfig {
  /** Symbol / pair name shown in the toolbar. E.g. 'BTC/USDT'. */
  symbol?: string;

  /** Chart theme. Default: 'dark'. */
  theme?: 'dark' | 'light';

  /** Initial candle style. Default: 'classic'. */
  style?: CandleStyle;

  /** Initial OHLC data. */
  data?: OHLCTuple[];

  /** Initial volume data (must match data length). */
  volumes?: number[];

  /** Show volume bars. Default: true. */
  volume?: boolean;

  /** Show toolbar. Pass true for all features, or a config object. */
  toolbar?: boolean | ToolbarConfig;

  /** Show chart navigation (zoom/pan/reset) overlay. Default: true. */
  chartNav?: boolean;

  /** Initial overlays (SMA, EMA, Bollinger, etc.). */
  overlays?: OverlayConfig[];

  /** Initial sub-panes (RSI, MACD, etc.). */
  subPanes?: SubPaneConfig[];

  /** Enable auto-scroll when new data arrives. Default: true. */
  autoScroll?: boolean;

  /** Streaming configuration. */
  streaming?: StreamingConfig;

  /** Custom data source adapter. */
  dataSource?: DataSource;

  /** Called when timeframe is changed via toolbar. */
  onTimeframeChange?: (timeframe: TimeframeInfo) => void;

  /** Called when the widget is fully initialized. */
  onReady?: (widget: DiWidget) => void;
}

// ---------------------------------------------------------------------------
// Widget instance
// ---------------------------------------------------------------------------

export interface DiWidget {
  /** Feed a raw tick (aggregated into candles by the SDK). */
  addTick(tick: TickInput): void;

  /** Append a completed candle. */
  appendCandle(candle: OHLCTuple): void;

  /** Update the last (forming) candle. */
  updateLastCandle(candle: OHLCTuple): void;

  /** Append a volume value. */
  appendVolume(volume: number): void;

  /** Replace all candle data. */
  setData(candles: OHLCTuple[]): void;

  /** Replace all volume data. */
  setVolumes(volumes: number[]): void;

  /** Change theme. */
  setTheme(theme: 'dark' | 'light'): void;

  /** Change candle style. */
  setStyle(style: CandleStyle): void;

  /** Access the underlying DiChart instance. */
  readonly chart: DiChartInstance;

  /** Subscribe to a widget event. */
  on<K extends import('./events').WidgetEventName>(
    event: K,
    fn: (...args: import('./events').WidgetEventMap[K]) => void,
  ): void;

  /** Unsubscribe from a widget event. */
  off<K extends import('./events').WidgetEventName>(
    event: K,
    fn: (...args: import('./events').WidgetEventMap[K]) => void,
  ): void;

  /** Destroy the widget and clean up DOM. */
  dispose(): void;
}

// ---------------------------------------------------------------------------
// Timeframe mapping
// ---------------------------------------------------------------------------

export interface TimeframeInfo {
  key: string;
  label: string;
  intervalMs: number;
}

export const TIMEFRAMES: TimeframeInfo[] = [
  { key: '1s', label: '1s', intervalMs: 1_000 },
  { key: '1m', label: '1m', intervalMs: 60_000 },
  { key: '5m', label: '5m', intervalMs: 5 * 60_000 },
  { key: '15m', label: '15m', intervalMs: 15 * 60_000 },
  { key: '1h', label: '1H', intervalMs: 60 * 60_000 },
  { key: '4h', label: '4H', intervalMs: 4 * 60 * 60_000 },
  { key: '1d', label: '1D', intervalMs: 24 * 60 * 60_000 },
];

// ---------------------------------------------------------------------------
// Indicator definitions
// ---------------------------------------------------------------------------

export interface IndicatorDef {
  id: string;
  name: string;
  description?: string;
  category: 'overlay' | 'oscillator';
  color: string;
  defaultConfig: OverlayConfig | SubPaneConfig;
}

export const INDICATORS: IndicatorDef[] = [
  { id: 'sma', name: 'SMA', description: '(20)', category: 'overlay', color: '#FFD700', defaultConfig: { id: 'sma20', type: 'sma', period: 20, color: '#FFD700' } },
  { id: 'ema', name: 'EMA', description: '(9)', category: 'overlay', color: '#FF6B6B', defaultConfig: { id: 'ema9', type: 'ema', period: 9, color: '#FF6B6B' } },
  { id: 'bb', name: 'Bollinger', description: '(20, 2)', category: 'overlay', color: '#4ECDC4', defaultConfig: { id: 'bb20', type: 'bollinger', period: 20, stdDev: 2, color: '#4ECDC4', bandColor: '#8888FF' } },
  { id: 'vwap', name: 'VWAP', category: 'overlay', color: '#FF9800', defaultConfig: { id: 'vwap', type: 'vwap', color: '#FF9800' } },
  { id: 'ichimoku', name: 'Ichimoku', category: 'overlay', color: '#E91E63', defaultConfig: { id: 'ichimoku', type: 'ichimoku', color: '#E91E63', tenkanPeriod: 9, kijunPeriod: 26, senkouPeriod: 52, displacement: 26 } },
  { id: 'rsi', name: 'RSI', description: '(14)', category: 'oscillator', color: '#A78BFA', defaultConfig: { id: 'rsi', type: 'rsi', period: 14, height: 120 } },
  { id: 'macd', name: 'MACD', description: '(12,26,9)', category: 'oscillator', color: '#4ECDC4', defaultConfig: { id: 'macd', type: 'macd', height: 130, fastPeriod: 12, slowPeriod: 26, signalPeriod: 9 } },
  { id: 'stochastic', name: 'Stochastic', description: '(14)', category: 'oscillator', color: '#00BCD4', defaultConfig: { id: 'stochastic', type: 'stochastic', height: 120, period: 14, smoothK: 3, smoothD: 3 } },
  { id: 'atr', name: 'ATR', description: '(14)', category: 'oscillator', color: '#8BC34A', defaultConfig: { id: 'atr', type: 'atr', height: 100, period: 14 } },
  { id: 'obv', name: 'OBV', category: 'oscillator', color: '#9C27B0', defaultConfig: { id: 'obv', type: 'obv', height: 100 } },
  { id: 'adx', name: 'ADX', description: '(14)', category: 'oscillator', color: '#FF5722', defaultConfig: { id: 'adx', type: 'adx', height: 120, period: 14 } },
];

export const CANDLE_STYLES: { value: CandleStyle; label: string }[] = [
  { value: 'classic', label: 'Candlestick' },
  { value: 'hollow', label: 'Hollow' },
  { value: 'heikin-ashi', label: 'Heikin-Ashi' },
  { value: 'bar', label: 'Bar Chart' },
  { value: 'line', label: 'Line Chart' },
  { value: 'area', label: 'Area Chart' },
  { value: 'baseline', label: 'Baseline' },
  { value: 'renko', label: 'Renko' },
];
